import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { isTokenExpired } from "../utils/jwt";
import { getMe, logoutUser, refreshAccessToken } from "../services/authService";
import { toCamelCaseObject } from "../utils/camel";
import { AUTH_STORAGE_KEY, AUTH_SESSION_EXPIRED_EVENT } from "../utils/api";

const AuthContext = createContext(null);
export const REFRESH_KEY = "prombiz_refresh_token";
const ACTIVE_BUSINESS_KEY = "prombiz_active_business";

/**
 * Merge enriched auth context from backend (businesses, memberships, invitations, routing)
 * into the user state object.
 */
function mergeAuthContext(userObj, response) {
  if (response.businesses) {
    userObj.businesses = response.businesses.map(b => toCamelCaseObject(b));
  }
  if (response.memberships) {
    userObj.memberships = response.memberships.map(m => toCamelCaseObject(m));
  }
  if (response.pendingInvitations) {
    userObj.pendingInvitations = response.pendingInvitations.map(i => toCamelCaseObject(i));
  }
  if (response.routing) {
    userObj.routing = response.routing;
  }
  userObj.hasClientProfile = !!response.hasClientProfile;

  // Backward compat: set .business to first owned business or first membership
  if (response.business) {
    userObj.business = toCamelCaseObject(response.business);
  } else if (response.businesses?.length) {
    userObj.business = toCamelCaseObject(response.businesses[0]);
  }

  return userObj;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBusinessId, setActiveBusinessIdState] = useState(() => {
    const stored = localStorage.getItem(ACTIVE_BUSINESS_KEY);
    return stored ? Number(stored) : null;
  });

  const setActiveBusiness = useCallback((id, userState) => {
    const u = userState || user;
    // Validate that the business ID is accessible to the user
    if (id && u) {
      const allIds = [
        ...(u.businesses || []).map(b => b.id),
        ...(u.memberships || []).map(m => m.businessId),
      ];
      if (allIds.length > 0 && !allIds.includes(Number(id))) {
        console.warn("[AuthContext] activeBusinessId", id, "not in user's business list — ignoring");
        return;
      }
    }
    setActiveBusinessIdState(id);
    if (id) {
      localStorage.setItem(ACTIVE_BUSINESS_KEY, String(id));
    } else {
      localStorage.removeItem(ACTIVE_BUSINESS_KEY);
    }
  }, [user]);

  // Listen for 401 from API
  useEffect(() => {
    const handleSessionExpired = () => { setUser(null); setToken(null); };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  // Initialize auth state
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem(AUTH_STORAGE_KEY);

      if (storedToken) {
        if (isTokenExpired(storedToken)) {
          const rt = localStorage.getItem(REFRESH_KEY);
          if (rt) {
            try {
              const data = await refreshAccessToken(rt);
              localStorage.setItem(AUTH_STORAGE_KEY, data.accessToken);
              if (data.refreshToken) localStorage.setItem(REFRESH_KEY, data.refreshToken);
            } catch (e) {
              localStorage.removeItem(AUTH_STORAGE_KEY);
              localStorage.removeItem(REFRESH_KEY);
              setToken(null); setUser(null); setLoading(false);
              return;
            }
          } else {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            setToken(null); setUser(null); setLoading(false);
            return;
          }
        }

        const currentToken = localStorage.getItem(AUTH_STORAGE_KEY);
        setToken(currentToken);

        try {
          const response = await getMe();
          if (response.success) {
            const userObj = mergeAuthContext(toCamelCaseObject(response.user), response);
            setUser(userObj);
            // Auto-set active business if not set
            if (!activeBusinessId && userObj.routing?.businessId) {
              setActiveBusiness(userObj.routing.businessId);
            }
          } else {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            setToken(null); setUser(null);
          }
        } catch (error) {
          console.error("Error fetching user:", error);
          localStorage.removeItem(AUTH_STORAGE_KEY);
          setToken(null); setUser(null);
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const logout = async () => {
    try { await logoutUser(); } catch (_) {}
    setUser(null); setToken(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ACTIVE_BUSINESS_KEY);
  };

  /**
   * Login — stores user + token + enriched context.
   * @param {object} userData - user object from server
   * @param {string} authToken - JWT access token
   * @param {string} refreshToken - refresh token
   * @param {object} authContext - enriched context (businesses, memberships, routing, etc.)
   * @returns {boolean} success
   */
  const login = (userData, authToken, refreshToken, authContext = {}) => {
    if (isTokenExpired(authToken)) {
      console.error("Cannot login with expired token");
      return false;
    }

    const userObj = mergeAuthContext(toCamelCaseObject(userData), authContext);
    setUser(userObj);
    setToken(authToken);
    localStorage.setItem(AUTH_STORAGE_KEY, authToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);

    // Auto-set active business
    if (authContext.routing?.businessId) {
      setActiveBusiness(authContext.routing.businessId);
    }

    return true;
  };

  const refreshUserToken = async () => {
    const rt = localStorage.getItem(REFRESH_KEY);
    if (!rt) return false;
    try {
      const data = await refreshAccessToken(rt);
      setToken(data.accessToken);
      localStorage.setItem(AUTH_STORAGE_KEY, data.accessToken);
      if (data.refreshToken) localStorage.setItem(REFRESH_KEY, data.refreshToken);
      return true;
    } catch (e) {
      setUser(null); setToken(null);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      localStorage.removeItem(REFRESH_KEY);
      return false;
    }
  };

  const updateUser = (userData) => {
    setUser((prev) => (prev ? { ...prev, ...toCamelCaseObject(userData) } : toCamelCaseObject(userData)));
  };

  const refreshUser = async () => {
    const response = await getMe();
    if (response.success) {
      const userObj = mergeAuthContext(toCamelCaseObject(response.user), response);
      setUser(userObj);
    }
  };

  // Token expiry check (every 5 minutes)
  useEffect(() => {
    if (!token) return;
    const checkTokenExpiry = async () => {
      if (isTokenExpired(token)) {
        const refreshed = await refreshUserToken();
        if (!refreshed) logout();
      }
    };
    checkTokenExpiry();
    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user && !!token && !isTokenExpired(token),
    activeBusinessId,
    setActiveBusiness,
    login,
    logout,
    updateUser,
    refreshUser,
    refreshUserToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
